// Числата на правилото за взаимозаменяемост — чисти функции без React/DOM
// (закотвени в `interchangeRuleMath.test.ts`).
//
// Правилото се ПОКАЗВА като равенство: „1 кг Кафе A = 0.7 кг Кафе B = 1.2 кг
// Кафе C" — всеки продукт има КОЛИЧЕСТВО (до 3 знака) и всички количества са
// равностойни. Записва се обаче КОЕФИЦИЕНТ: „колко базови единици на групата е
// 1 единица от продукта" (BE `InterchangeMember.coefficient`). Двете са
// обратнопропорционални: количество_i = W / коефициент_i за някакво общо W.
// Машината за замени гледа САМО отношенията (BE `_interchange_map`:
// 1 донор = коеф_донор / коеф_получател получател; FE
// `buildSubstitutionProposals` — същото), затова мащабът на коефициентите е
// свободен и тук се избира така, че записът да е ТОЧЕН.

const AMOUNT_SCALE = 1000; // количествата са до 3 знака след точката
// Numeric(12,4) в BE → до 8 цели цифри
const MAX_COEFFICIENT = 99_999_999;

export const roundAmount = (value: number): number =>
  Math.round(value * AMOUNT_SCALE) / AMOUNT_SCALE;

const isWholeAmount = (value: number) =>
  Math.abs(roundAmount(value) - value) <= 1e-9 * Math.max(1, Math.abs(value));

const isPositive = (value: number | null | undefined): value is number =>
  typeof value === "number" && Number.isFinite(value) && value > 0;

/**
 * Количествата за показване от записаните коефициенти (редакция и списъкът).
 *
 * „Мярката" (количеството 1) е ПЪРВИЯТ продукт, спрямо който всички останали
 * излизат с до 3 знака — така записаното от този редактор (цели коефициенти,
 * `coefficientsFromAmounts`) се показва отново точно както е въведено
 * („1 кг A = 0.7 кг B" → коеф. 7 и 10 → пак „1 = 0.7", дори B да е пръв по
 * приоритет). Стари групи без такъв продукт — спрямо първия, закръглено.
 */
export const amountsFromCoefficients = (
  coefficients: (number | null | undefined)[],
): (number | null)[] => {
  const chainFrom = (anchor: number) =>
    coefficients.map((coefficient) => (isPositive(coefficient) ? anchor / coefficient : null));
  let anchor: number | null = null;
  for (const coefficient of coefficients) {
    if (!isPositive(coefficient)) continue;
    if (chainFrom(coefficient).every((amount) => amount == null || isWholeAmount(amount))) {
      anchor = coefficient;
      break;
    }
  }
  anchor ??= coefficients.find(isPositive) ?? null;
  if (anchor == null) return coefficients.map(() => null);
  // Под 0.0005 закръгленото става 0 → празно поле, което иска стойност
  return chainFrom(anchor).map((amount) => (amount == null ? null : roundAmount(amount) || null));
};

const greatestCommonDivisor = (a: number, b: number): number => {
  let x = a;
  let y = b;
  while (y) [x, y] = [y, x % y];
  return x;
};

/**
 * Коефициентите за запис от количествата. Количество_i = n_i / 1000 (цяло n_i),
 * коефициент_i = НОК(n) / n_i → ВЗАИМНО ПРОСТИ ЦЕЛИ числа, които пазят
 * отношението ТОЧНО: „1 кг A = 0.7 кг B" → 7 и 10; „1 кашон = 24 бр" → 24 и 1;
 * „1 = 1 = 1" → 1, 1, 1. Приближение (4 знака, възможно най-едър мащаб) само
 * ако точните числа не се побират в колоната.
 */
export const coefficientsFromAmounts = (amounts: number[]): number[] => {
  const scaled = amounts.map((amount) => Math.max(1, Math.round(amount * AMOUNT_SCALE)));
  let leastCommonMultiple = 1;
  for (const value of scaled) {
    const factor = value / greatestCommonDivisor(leastCommonMultiple, value);
    if (leastCommonMultiple > Number.MAX_SAFE_INTEGER / factor) {
      leastCommonMultiple = Number.NaN;
      break;
    }
    leastCommonMultiple *= factor;
  }
  if (Number.isFinite(leastCommonMultiple)) {
    const exact = scaled.map((value) => leastCommonMultiple / value);
    if (Math.max(...exact) <= MAX_COEFFICIENT) return exact;
  }
  // Приближение: най-малкото количество получава най-големия възможен кръгъл
  // коефициент — колкото по-едри числата, толкова по-малка грешката от 4-те знака.
  const smallest = Math.min(...scaled);
  const power = Math.floor(Math.log10((MAX_COEFFICIENT * smallest) / AMOUNT_SCALE));
  const worth = 10 ** power;
  return scaled.map((value) =>
    Math.max(0.0001, Math.round(((worth * AMOUNT_SCALE) / value) * 10_000) / 10_000),
  );
};

/**
 * Коефициентите, които отиват в payload-а, или null при празно/нулево
 * количество. Непипнатата група (никое количество не е сменяно и не е добавян
 * продукт — `savedCoefficient` е жив на всеки ред) пази ЗАПИСАНИТЕ стойности
 * до последния знак; иначе всички се смятат наново от показаните количества.
 */
export const coefficientsForSave = (
  members: { amount: number | null; savedCoefficient: number | null }[],
): number[] | null => {
  if (!members.every((member) => isPositive(member.amount))) return null;
  if (members.every((member) => isPositive(member.savedCoefficient)))
    return members.map((member) => member.savedCoefficient as number);
  return coefficientsFromAmounts(members.map((member) => member.amount as number));
};

// ─── Приоритет ─────────────────────────────────────────────────────────────
// „oldest" — без приоритет (sortIndex null): първо продуктът с най-старата
// доставка. „order" — по реда на картите (sortIndex 1, 2, 3…).
export type PriorityMode = "oldest" | "order";

/**
 * sortIndex за всеки член. Заредена група с приоритет, чийто ред НЕ е пипан,
 * праща записаните стойности непроменени (смесените [1, null, null] и
 * равните индекси запазват смисъла си); пипнатият ред се номерира наново.
 */
export const sortIndexesForSave = (
  members: { savedSortIndex: number | null }[],
  mode: PriorityMode,
  orderTouched: boolean,
): (number | null)[] => {
  if (mode === "oldest") return members.map(() => null);
  if (orderTouched) return members.map((_, index) => index + 1);
  return members.map((member) => member.savedSortIndex);
};

// ─── Живият пример „Ако липсват …" ─────────────────────────────────────────
export type PreviewDonor<M> = {
  member: M;
  // колко от донора се изписва (в неговите единици); null — липсва количество
  quantity: number | null;
};

/**
 * Какво предлага системата, ако липсват `shortage` единици от `recipient`:
 * донорите на СТЪПКИ по приоритета (sortIndex възх., без приоритет — накрая,
 * както в BE и при разнасяне). Донори с еднакъв приоритет (вкл. всички без)
 * са ЕДНА стъпка — между тях решава най-старата доставка.
 */
export const previewSteps = <M extends { amount: number | null }>(
  members: M[],
  sortIndexes: (number | null)[],
  recipientIndex: number,
  shortage: number,
): PreviewDonor<M>[][] => {
  const recipientAmount = members[recipientIndex]?.amount;
  const donors = members
    .map((member, index) => ({ member, index, sortIndex: sortIndexes[index] ?? null }))
    .filter((donor) => donor.index !== recipientIndex)
    .sort(
      (a, b) =>
        (a.sortIndex ?? Number.POSITIVE_INFINITY) - (b.sortIndex ?? Number.POSITIVE_INFINITY) ||
        a.index - b.index,
    );
  const steps: PreviewDonor<M>[][] = [];
  let previousSortIndex: number | null | undefined;
  for (const donor of donors) {
    const quantity =
      isPositive(recipientAmount) && isPositive(donor.member.amount)
        ? (shortage * donor.member.amount) / recipientAmount
        : null;
    if (steps.length && donor.sortIndex === previousSortIndex)
      steps[steps.length - 1].push({ member: donor.member, quantity });
    else steps.push([{ member: donor.member, quantity }]);
    previousSortIndex = donor.sortIndex;
  }
  return steps;
};
